import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Select, Typography } from "@mui/material";
import { BRAND_COLORS } from "../../../shared/OAWorkflow/constants/taskStatuses";
import { useEffect, useMemo, useRef, useState } from "react";
import MapFieldsStep from "./MapFieldsStep";
import { sanitizeFileName } from "../utils/assistantUtils";

// Custom Forms are built on one of two combined datasets, each covering the
// Bibliographic Data Extractor jurisdictions JUNE already supports. The
// backend's Custom Forms endpoints (controllers/customForms.controller.js in
// patent-asistant-apis) key everything off the dataset id below; a dataset is
// offered when the tenant can use at least one of its BDE intents (read from
// the existing "Bibliographic Data Extraction" intent list, so it stays in
// sync with the tenant's subscriptions).
const DATASETS = [
    {
        database: "patent",
        label: "Patent",
        intentNames: ["patentbibliointent", "austpatentbibliointent", "epobibliointent", "globalpatentintent"]
    },
    {
        database: "trademark",
        label: "Trademark",
        intentNames: ["trademarkbibliointent", "austtrademarkbibliointent"]
    }
];

// How wide the Map Fields modal has to be for SuperDoc's comment UI to clear
// the document, derived from what SuperDoc itself lays out (the measurements
// behind these numbers are in DocxEditor.css and
// scripts/patch-superdoc-template-builder.js). Named rather than inlined
// because the same arithmetic is referenced from both of those files.
const PAGE_PX = 816;                 // a Letter page at SuperDoc's default zoom
const COMMENT_POPOVER_PX = 320;      // .superdoc__compact-comment-popover
const POPOVER_GUTTER_PX = 24;        // clearance between page and popover
const FIELDS_SIDEBAR_PX = 244;       // .docx-fields-sidebar

// Editor width at which the popover stops overlapping the page, and the modal
// width that implies once the field sidebar is taken out.
const COMMENTS_MIN_EDITOR_PX = PAGE_PX + COMMENT_POPOVER_PX + POPOVER_GUTTER_PX;   // 1160
const COMMENTS_MIN_MODAL_PX = COMMENTS_MIN_EDITOR_PX + FIELDS_SIDEBAR_PX;          // 1404

const COMMENTS_MAX_MODAL_PX = 1440;

const COMMENTS_WIDTH_PCT = 0.95;


const useViewportWidth = () => {
    const [width, setWidth] = useState(() => window.innerWidth);

    useEffect(() => {
        const onResize = () => setWidth(window.innerWidth);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    return width;
};

const CreateCustomFormModal = ({
    open,
    onClose,
    onSaved,
    handleModuleSubmit,
    intentList,
    originatingRequestId,
    requestId
}) => {
    const [uploadFiles, setUploadFiles] = useState([]);
    const [uploadError, setUploadError] = useState("");
    const [uploadDragOver, setUploadDragOver] = useState(false);
    const [selectedDataset, setSelectedDataset] = useState("");
    const [step, setStep] = useState("upload");
    const [hasComments, setHasComments] = useState(false);
    const viewportWidth = useViewportWidth();
    const uploadFileInputRef = useRef(null);
    const datasetOptions = useMemo(() => {
        const usableIntents = (intentList["Bibliographic Data Extraction"] || []).filter(
            (intent) => !intent.isHidden && intent.isActive && intent.tenantSubscribed && intent.userPermission
        );
        return DATASETS.map((dataset) => ({
            ...dataset,
            enabled: usableIntents.some((intent) => dataset.intentNames.includes(intent.intentName?.toLowerCase()))
        }));
    }, [intentList]);

    const resetLocalState = () => {
        setUploadFiles([]);
        setUploadError("");
        setUploadDragOver(false);
        setSelectedDataset("");
        setStep("upload");
    };

    const handleCloseUpload = () => {
        resetLocalState();
        onClose();
    };

    const handleSaved = () => {
        resetLocalState();
        onSaved?.();
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setUploadDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setUploadDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setUploadDragOver(false);
        if (e.dataTransfer.files) {
            addUploadFiles(Array.from(e.dataTransfer.files));
        }
    };

    const addUploadFiles = (newFiles) => {
        const allowedTypes = [".doc", ".docx"];
        const maxFiles = 1;
        const maxSize = 25 * 1024 * 1024; // 25 MB, matches TrexoChat.js file upload limit

        let validFiles = newFiles.filter(file => {
            const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
            if (!allowedTypes.includes(ext)) {
                setUploadError("Invalid file type. Only .doc and .docx files are allowed.");
                return false;
            }
            if (file.size > maxSize) {
                setUploadError(`File ${file.name} exceeds 25 MB limit.`);
                return false;
            }
            return true;
        });

        if (uploadFiles.length + validFiles.length > maxFiles) {
            setUploadError(`Maximum ${maxFiles} files allowed.`);
            validFiles = validFiles.slice(0, maxFiles - uploadFiles.length);
        }

        if (validFiles.length > 0) {
            setUploadError("");
            const sanitizedFiles = validFiles.map((file) => {
                const sanitizedName = sanitizeFileName(file.name);
                return sanitizedName === file.name
                    ? file
                    : new File([file], sanitizedName, { type: file.type, lastModified: file.lastModified });
            });
            setUploadFiles(prev => [...prev, ...sanitizedFiles]);
        }
    };

    const removeUploadFile = (index) => {
        setUploadFiles(prev => prev.filter((_, i) => i !== index));
        if (uploadFileInputRef.current) {
            uploadFileInputRef.current.value = "";
        }
    };

    const handleNext = () => {
        if (uploadFiles.length === 0) {
            setUploadError("Please select a file to upload");
            return;
        }
        if (!selectedDataset) {
            setUploadError("Please select a dataset");
            return;
        }
        if (!datasetOptions.find((dataset) => dataset.database === selectedDataset)?.enabled) {
            setUploadError("This dataset isn't available for your workspace.");
            return;
        }

        setStep("mapFields");
    };

    if (step === "mapFields") {
        if (!open) return null;
        const database = selectedDataset;


        const commentsFitAtFullWidth = viewportWidth * COMMENTS_WIDTH_PCT >= COMMENTS_MIN_MODAL_PX;
        const modalWidth = hasComments
            ? (commentsFitAtFullWidth ? `${COMMENTS_WIDTH_PCT * 100}%` : "100%")
            : "90%";
        const modalMaxWidth = hasComments
            ? (commentsFitAtFullWidth ? `${COMMENTS_MAX_MODAL_PX}px` : "100%")
            : "1200px";

        return (
            <Box
                sx={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 1300,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(15, 23, 42, 0.5)"
                }}
            >
                <Box
                    sx={{

                        width: modalWidth,
                        maxWidth: modalMaxWidth,
                        height: "85vh",
                        backgroundColor: "#fff",
                        borderRadius: "12px",
                        overflow: "hidden",
                        boxShadow: 24
                    }}
                >
                    <MapFieldsStep
                        file={uploadFiles[0]}
                        database={database}
                        onClose={handleCloseUpload}
                        onCommentsChange={setHasComments}
                        onSaved={handleSaved}
                        handleModuleSubmit={handleModuleSubmit}
                        originatingRequestId={originatingRequestId}
                        requestId={requestId}
                    />
                </Box>
            </Box>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={handleCloseUpload}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: "12px" } }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: "1px solid #E2E8F0", px: 3, py: 2 }}>
                <Box display={'flex'} flexDirection={'column'}>
                    <DialogTitle sx={{ p: 0, fontWeight: 700, fontSize: "18px" }}>
                        Upload Document
                    </DialogTitle>
                    <Typography sx={{ fontSize: "12px", fontWeight: 400 }}>
                        Upload a Word document to use as your form template
                    </Typography>
                </Box>
                <IconButton size="small" onClick={handleCloseUpload} sx={{ color: "#94A3B8" }}>
                    <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </Box>
            <DialogContent sx={{ pt: "20px !important", pb: 3 }}>
                <Box>
                    {uploadFiles.length === 0 ? (
                        <Box
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => uploadFileInputRef.current?.click()}
                            sx={{
                                border: `2px dashed ${uploadDragOver ? BRAND_COLORS.PRIMARY : "#CBD5E1"}`,
                                borderRadius: "8px",
                                backgroundColor: uploadDragOver ? "#EAF2FF" : "#F8FAFC",
                                p: 4,
                                textAlign: "center",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                mb: 3,
                                "&:hover": {
                                    borderColor: BRAND_COLORS.PRIMARY,
                                    backgroundColor: "#F2F7FC"
                                }
                            }}
                        >
                            <CloudUploadOutlinedIcon sx={{ fontSize: 40, color: BRAND_COLORS.PRIMARY, mb: 1 }} />
                            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#334155", mb: 0.5 }}>
                                Drag & drop files here, or click to browse
                            </Typography>
                            <Typography sx={{ fontSize: "12px", color: "#94A3B8" }}>
                                DOC or DOCX (max. 25MB)
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ mb: 3 }}>
                            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#475569", mb: 1 }}>
                                Template Document
                            </Typography>
                            {uploadFiles.map((f, idx) => (
                                <Box key={idx} sx={{
                                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5,
                                    backgroundColor: "#F0FDF4", border: "1px solid #86EFAC",
                                    borderRadius: "8px", p: 1.5
                                }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <DescriptionOutlinedIcon sx={{ color: BRAND_COLORS.PRIMARY, fontSize: 22 }} />
                                        <Box>
                                            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1E293B" }}>
                                                {f.name}
                                            </Typography>
                                            <Typography sx={{ fontSize: "11px", color: "#94A3B8" }}>
                                                {Math.round(f.size / 1024)} KB
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <HowToRegOutlinedIcon sx={{ color: "#16A34A", fontSize: 20 }} />
                                        <CloseIcon
                                            sx={{ fontSize: 16, color: "#94A3B8", cursor: "pointer", "&:hover": { color: "#EF4444" } }}
                                            onClick={() => removeUploadFile(idx)}
                                        />
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}

                    <input
                        ref={uploadFileInputRef}
                        type="file"
                        multiple
                        accept=".doc,.docx"
                        onChange={(e) => {
                            addUploadFiles(Array.from(e.target.files));
                            e.target.value = "";
                        }}
                        style={{ display: "none" }}
                    />

                    {uploadFiles.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#475569", mb: 1 }}>
                                Select Dataset
                            </Typography>
                            <Select
                                fullWidth
                                displayEmpty
                                value={selectedDataset}
                                onChange={(e) => setSelectedDataset(e.target.value)}
                                sx={{ fontSize: "14px" }}
                                renderValue={(value) => {
                                    if (!value) {
                                        return <Typography sx={{ fontSize: "14px", color: "#94A3B8" }}>Select Dataset</Typography>;
                                    }
                                    return datasetOptions.find((dataset) => dataset.database === value)?.label;
                                }}
                            >
                                {datasetOptions.map((dataset) => (
                                    <MenuItem
                                        key={dataset.database}
                                        value={dataset.database}
                                        disabled={!dataset.enabled}
                                    >
                                        {dataset.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    )}

                    {uploadError && (
                        <Alert severity="error" sx={{ mt: 2, borderRadius: "6px" }}>
                            {uploadError}
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #E2E8F0", justifyContent: "flex-end" }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleNext}
                    disabled={uploadFiles.length === 0 || !selectedDataset}
                    sx={{ fontSize: "14px", fontWeight: 600, px: 3 }}
                >
                    Next
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateCustomFormModal;